import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import type { ActivityCategory } from '@/types/activity';

interface CategoryDropdownProps {
  categories: ActivityCategory[];
  selectedCategoryId: number | null;
  onSelect: (categoryId: number | null) => void;
}

export default function CategoryDropdown({
  categories,
  selectedCategoryId,
  onSelect,
}: CategoryDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = categories.find((c) => parseInt(c.id, 10) === selectedCategoryId) ?? null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-admin-dark-700 border border-admin-slate-300/10 rounded-md text-admin-slate-400 hover:border-admin-slate-300/20 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors text-sm"
      >
        <span className="flex items-center gap-2 min-w-0">
          {selected?.color && (
            <span
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: selected.color }}
            />
          )}
          <span className="truncate">{selected ? selected.name : 'No category'}</span>
        </span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-admin-slate-300/20 bg-admin-dark-800 shadow-lg">
          <button
            type="button"
            onClick={() => { onSelect(null); setOpen(false); }}
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-admin-dark-700 transition-colors text-left text-sm text-admin-slate-400"
          >
            No category
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => { onSelect(parseInt(cat.id, 10)); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-admin-dark-700 transition-colors text-left text-sm text-admin-slate-400"
            >
              {cat.color && (
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: cat.color }}
                />
              )}
              <span>{cat.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
